'use client';

import Image from 'next/image';
import { useState } from 'react';

import { SectionWrapper } from '@/components/atomic/nuclei';
import { DEVICE_SURFACE } from '@/config/common';
import { PARTNER_CATEGORIES } from '@/config/landingPage';
import { GetUiDeviceSurface } from '@/lib/screenResolution';

const PartnerCategoryIconMobile = ({
  activeCategory,
  category,
  setActiveCategory
}) => (
  <button
    className={`flex flex-col items-center self-center rounded-lg border w-20 ${
      activeCategory.title === category.title
        ? 'border-brand p-4'
        : 'px-2 py-2 border-neutral-200'
    }`}
    key={category.title}
    onClick={() => setActiveCategory(category)}
  >
    <Image
      alt={category.title}
      className='h-10 w-10 object-contain'
      height={0}
      style={{
        height: 40,
        width: 40
      }}
      src={category.icon}
      unoptimized
      width={0}
    />
    {activeCategory.title !== category.title && (
      <span className='mt-1 text-[8px] leading-[14px] font-medium bg-brand-gradient bg-clip-text text-transparent'>
        {category.title}
      </span>
    )}
  </button>
);

const PartnerCategoryCardMobile = ({ activeCategory }) => (
  <div className='col-span-9'>
    <div className='rounded-2xl border border-pink-200 shadow-sm p-5 sm:p-6 md:p-7'>
      <div className='text-base font-semibold text-neutral-900'>
        {activeCategory.title}
      </div>

      <div className='mt-4 flex flex-wrap gap-2'>
        {activeCategory.tags.map((tagName) => (
          <span
            className='px-3 py-1 text-[10px] rounded-md border border-brand bg-brand-gradient bg-clip-text text-transparent'
            key={tagName}
          >
            {tagName}
          </span>
        ))}
      </div>

      <div className='mt-4 text-neutral-700 text-xs leading-5'>
        {activeCategory.description}
      </div>

      <ul className='mt-5 space-y-2 text-neutral-700 text-xs'>
        {activeCategory.expandedItems.map((expandedItem) => (
          <li
            className='flex items-start gap-3'
            key={expandedItem}
          >
            <span className='self-center inline-block h-2 w-2 rounded-full bg-neutral-300' />
            <span>{expandedItem}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PartnerCategoriesMobile = () => {
  const [activeCategory, setActiveCategory] = useState(PARTNER_CATEGORIES[0]);

  return (
    <div className='relative grid grid-cols-12 gap-4'>
      <div className='col-span-12 flex justify-between gap-2'>
        {PARTNER_CATEGORIES.slice(0, 4).map((category, index) => (
          <PartnerCategoryIconMobile
            key={index}
            {...{ activeCategory, category, setActiveCategory }}
          />
        ))}
      </div>
      <PartnerCategoryCardMobile {...{ activeCategory }} />
      <div className='col-span-3 flex flex-col gap-3'>
        {PARTNER_CATEGORIES.slice(4, 8).map((category, index) => (
          <PartnerCategoryIconMobile
            key={index + 4}
            {...{ activeCategory, category, setActiveCategory }}
          />
        ))}
      </div>
    </div>
  );
};

const PartnerCategoryCardWeb = ({
  description,
  expandedItems = [],
  icon,
  isExpanded,
  onToggle,
  tags = [],
  title
}) => {
  return (
    <div
      className={`rounded-2xl shadow-md p-6 flex flex-col gap-4 transition-all duration-300 border border-pink-100 hover:shadow-lg hover:border-pink-200`}
    >
      {icon && (
        <div className='w-20 h-20 relative'>
          <Image
            alt={title}
            className='object-contain'
            fill
            src={icon}
          />
        </div>
      )}
      <div className='text-xl font-semibold text-gray-900'>{title}</div>
      {tags.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {tags.map((tag, idx) => (
            <span
              className='px-3 py-1 text-xs border border-brand bg-brand-gradient bg-clip-text text-transparent rounded-md'
              key={idx}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      {description && (
        <div className='text-gray-600 text-sm'>{description}</div>
      )}
      {isExpanded && (
        <ul className='list-disc list-inside text-neutral-500 space-y-2 mt-2 text-sm'>
          {expandedItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )}
      {expandedItems.length > 0 && (
        <button
          className='mt-4 bg-brand-gradient bg-clip-text text-transparent font-semibold flex items-center gap-1'
          onClick={onToggle}
        >
          {isExpanded ? 'Collapse' : 'View Services'} →
        </button>
      )}
    </div>
  );
};

const PartnerCategoriesWeb = ({ expandedIndex, handleToggle }) => (
  <div className='grid md:grid-cols-3 lg:grid-cols-4 gap-6 items-start'>
    {PARTNER_CATEGORIES.map((category, index) => (
      <PartnerCategoryCardWeb
        key={index}
        {...category}
        isExpanded={expandedIndex === index}
        onToggle={() => handleToggle(index)}
      />
    ))}
  </div>
);

const PartnerCategories = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const handleToggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const surface = GetUiDeviceSurface();
  const PartnerCategoriesComponentMap = {
    [DEVICE_SURFACE.WEB]: PartnerCategoriesWeb,
    [DEVICE_SURFACE.MOBILE]: PartnerCategoriesMobile
  };

  const PartnerCategoriesComponent = PartnerCategoriesComponentMap[surface];

  return (
    <SectionWrapper>
      <div className='text-center max-w-3xl mx-auto mb-12'>
        <div className='text-2xl xs:text-3xl sm:text-4xl font-bold leading-tight text-stroke-thin md:text-stroke-normal'>
          Partner{' '}
          <span className='bg-brand-gradient bg-clip-text text-transparent'>
            Categories
          </span>
        </div>
        <div className='mt-4 text-neutral-700 text-base md:text-xl'>
          Join thousands of service providers across multiple categories
        </div>
        <div className='flex justify-center mt-3 sm:mt-4 gap-2'>
          <div className='h-1 w-16 sm:w-20 bg-brand-gradient rounded-full' />
          <div className='h-1 w-4 sm:w-5 bg-brand-gradient rounded-full' />
        </div>
      </div>
      <PartnerCategoriesComponent
        {...{
          expandedIndex,
          handleToggle
        }}
      />
    </SectionWrapper>
  );
};

export default PartnerCategories;
